import styles from './flag.module.css';
import reactStringReplace from 'react-string-replace';
import classnames from 'classnames';
import React from 'react';
import { EVENT_NUMBER_MENTION_TRIGGER } from '../lexical/mention-plugin/constants';

/**
 *  for displaying
 *   !caution!
 *   ^vehicle^
 *   #0830230001
 *
 * NOTE: caution and vehicle are flag-node, created by flag-plugin.
 *       event number are mention-node, created by mention-plugin (require interaction to query from BE when create a new one),
 *       however when display event number in messaging, it's a static string, we only need some extra css to render the style,
 *       so use FlagRenderer to render event number.
 */

const CAUTION_REGEX = /!([^\s^!]*)\b!/;
// eslint-disable-next-line no-useless-escape
const VEHICLE_REGEX = /\^([^\s^(\^)]*)\b\^/;
const VALID_VEHICLE_REGEX = /(^((A[KLRZ]|C[AOT]|D[CE]|FL|GA|HI|I[ADLN]|K[SY]|LA|M[ADEINOST]|N[CDEHJMVY]|O[HKR]|PA|RI|S[CD]|T[NX]|UT|V[AT]|W[AIVY])-)?[\w]{1,17}$)/i;
const EVENT_NUMBER_REGEX = /(#[\w]{3,})/;

export function FlagRenderer({
    text,
    agencyEventNumberToIdMap,
    isFirstResponder,
    onAgencyEventNumberClick,
}: {
    text?: string;
    agencyEventNumberToIdMap?: { [key: string]: number };
    isFirstResponder?: boolean;
    onAgencyEventNumberClick?: ({
        agencyEventNumber,
        eventId,
    }: {
        agencyEventNumber: string;
        eventId: number;
    }) => void;
}): React.ReactElement {
    let textWithFlags = reactStringReplace(text, CAUTION_REGEX, (match, i) => (
        <span
            className={classnames(styles['flag'], styles['caution'])}
            data-testid="FLAG"
            key={`caution-${i}`}
        >
            {match}
        </span>
    ));

    // ensure vehicle key is unique; reactStringReplace doesn't guarantee unique index for array input text
    let i = 0;
    textWithFlags = reactStringReplace(textWithFlags, VEHICLE_REGEX, (match) =>
        VALID_VEHICLE_REGEX.exec(match) ? (
            <span
                className={classnames(styles['flag'], styles['vehicle'])}
                data-testid="FLAG"
                key={`vehicle-${i++}`}
            >
                {match}
            </span>
        ) : (
            match
        )
    );

    if (agencyEventNumberToIdMap && Object.keys(agencyEventNumberToIdMap).length > 0) {
        textWithFlags = reactStringReplace(
            textWithFlags,
            EVENT_NUMBER_REGEX,
            (possibleEventNumberWithPrefix, i) => {
                const agencyEventNumber = possibleEventNumberWithPrefix.replace(
                    EVENT_NUMBER_MENTION_TRIGGER,
                    ''
                );
                const eventId = agencyEventNumberToIdMap?.[agencyEventNumber];
                if (!eventId) {
                    // if eventId is not found in agencyEventNumberToIdMap, just return possibleEventNumberWithPrefix as raw text
                    return possibleEventNumberWithPrefix;
                }
                return (
                    <span
                        key={`event-number-${i}`}
                        className={classnames(
                            styles['flag'],
                            styles['event-number'],
                            isFirstResponder && styles['event-number-lg']
                        )}
                        data-testid="FLAG"
                        data-eventid={eventId}
                        onClick={() => {
                            onAgencyEventNumberClick?.({
                                agencyEventNumber,
                                eventId,
                            });
                        }}
                    >
                        <span>{possibleEventNumberWithPrefix}</span>
                    </span>
                );
            }
        );
    }

    return <span>{textWithFlags}</span>;
}
